import { useState } from "react";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Inbox, Clock, Leaf, UserRound, TrendingUp, TrendingDown, HeartPulse, Check, HandHeart } from "lucide-react";
import { toast } from "sonner";
import { format, subWeeks } from "date-fns";
import { Area, AreaChart, ResponsiveContainer, XAxis } from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProto } from "../ProtoContext";
import { LEADERS, OVERVIEW, leaderName } from "../data";
import { metrics, listFor, leaderStats, leaderSummary, COMFORTABLE_LOAD } from "../directory";
import { AssignButton, actionBtn } from "../Actions";
import { Avatar, Card, Eyebrow, fullName } from "../ui";
import { partOfDay } from "./Today";
import { cn } from "@/lib/utils";

const H = ({ children }) => <h2 className="t-heading">{children}</h2>;

const RhythmPicker = () => {
  const { rhythm, setRhythm } = useProto();
  return (
    <Popover>
      <PopoverTrigger asChild><button className="text-brand font-medium underline underline-offset-4" data-testid="overview-rhythm-change">Change</button></PopoverTrigger>
      <PopoverContent className="w-72 rounded-2xl bg-surface border-line p-4" data-testid="overview-rhythm-popover">
        <p className="t-name-sm">How often should someone receive a meaningful care touch?</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {[14, 30, 45, 60].map((d) => <button key={d} onClick={() => setRhythm(d)} className={cn("h-10 px-4 rounded-full text-sm font-medium", d === rhythm ? "bg-ink text-paper" : "bg-sunk text-ink")} data-testid={`rhythm-${d}`}>Every {d} days</button>)}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Tile = ({ label, value, move, dir, detail, onOpen, testid }) => {
  const Arrow = dir === "up" ? ArrowUpRight : ArrowDownRight;
  return (
    <button onClick={onOpen} className="text-left flex flex-col justify-start bg-paper p-5 sm:p-6 hover:bg-surface transition-colors group" data-testid={testid}>
      <p className="text-ink-soft text-[15px] leading-snug">{label}</p>
      <p className="t-metric mt-3" data-testid={`${testid}-value`}>{value}</p>
      <p className="trend mt-2.5" data-testid={`${testid}-move`}><Arrow size={15} />{move}</p>
      <p className="text-sm text-ink-faint mt-1 leading-snug">{detail}</p>
    </button>
  );
};

const scrollToNeeds = () => document.querySelector("[data-testid=overview-unassigned]")?.scrollIntoView({ behavior: "smooth", block: "center" });

const Summary = ({ m, waiting }) => {
  const { openList, rhythm, isPhone } = useProto();
  return (
    <section data-testid="overview-summary">
      <H>How are we doing?</H>
      <div className={cn("grid gap-px bg-line border-y border-line mt-5", isPhone ? "grid-cols-2" : "grid-cols-4")}>
        <Tile testid="metric-coverage" label="Care coverage" value={`${m.coveragePct}%`} dir="up" move="4% this month" detail={`${m.covered} of ${m.total} people have a caregiver`} onOpen={() => openList("covered")} />
        <Tile testid="metric-attention" label="People needing attention" value={m.attention} dir="down" move={`from ${m.attention + 5} last week`} detail={`Past due, or outside your ${rhythm}-day rhythm`} onOpen={() => openList("attention")} />
        <Tile testid="metric-month" label="Reached this month" value={m.month} dir="up" move="from 101 last month" detail="168 meaningful care connections logged" onOpen={() => openList("month")} />
        <Tile testid="metric-unassigned" label="Unassigned care needs" value={waiting} dir="down" move="from 5 last week" detail="Waiting for someone to take them" onOpen={scrollToNeeds} />
      </div>
    </section>
  );
};

const names = (key, r, a) => {
  const items = listFor(key, r, a).items;
  const first = items.slice(0, 2).map(fullName).join(", ");
  return items.length > 2 ? `${first} and ${items.length - 2} more` : first;
};

const GapRow = ({ icon: Icon, title, hint, onOpen, testid }) => (
  <button onClick={onOpen} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={testid}>
    <span className="h-11 w-11 rounded-full bg-sunk text-ink-soft flex items-center justify-center shrink-0"><Icon size={20} /></span>
    <span className="flex-1 min-w-0">
      <span className="block t-name-sm group-hover:text-brand transition-colors">{title}</span>
      <span className="block text-sm text-ink-faint mt-0.5">{hint}</span>
    </span>
    <ArrowRight size={18} className="text-ink-faint group-hover:text-brand shrink-0" />
  </button>
);

const Gaps = ({ m, waiting }) => {
  const { openList, rhythm, assigned } = useProto();
  return (
    <section data-testid="overview-gaps">
      <H>Where are the gaps?</H>
      <Card className="p-6 mt-4">
        <div className="flex gap-4" data-testid="overview-unassigned">
          <span className="h-11 w-11 rounded-full bg-brand-soft text-brand flex items-center justify-center shrink-0"><Inbox size={20} /></span>
          <div>
            <p className="t-name-sm" data-testid="overview-unassigned-title">{waiting ? `${waiting} care ${waiting === 1 ? "need is" : "needs are"} waiting for someone` : "Every new care need has someone"}</p>
            <p className="text-sm text-ink-faint mt-0.5">Shared by members, staff or prayer cards. No one has taken these yet.</p>
          </div>
        </div>
        <div className="mt-2 divide-y divide-line">
          {OVERVIEW.unassigned.map((u) => (
            <div key={u.id} className="flex items-center gap-3 py-3.5">
              <Avatar person={u} size={42} />
              <div className="flex-1 min-w-0"><p className="text-ink font-medium leading-tight">{u.name}</p><p className="text-sm text-ink-faint leading-snug">{u.reason} · {u.since}</p></div>
              <AssignButton item={u} />
            </div>
          ))}
        </div>
        <div className="mt-2 border-t border-line divide-y divide-line">
          <GapRow icon={Clock} testid="gap-pastdue" title={`${m.pastDue} follow-ups are past their date`} hint={names("pastdue", rhythm, assigned)} onOpen={() => openList("pastdue")} />
          <GapRow icon={Leaf} testid="gap-outside" title={`${m.outside} people are outside your care rhythm`} hint={names("outside", rhythm, assigned)} onOpen={() => openList("outside")} />
          <GapRow icon={UserRound} testid="gap-coverage" title={`${m.noCaregiver} people don't have a caregiver`} hint={names("coverage", rhythm, assigned)} onOpen={() => openList("coverage")} />
        </div>
      </Card>
    </section>
  );
};

const SERIES = {
  connected: { label: "People connected", data: [31, 33, 30, 35, 34, 36, 38, 37, 38, 41, 40, 44] },
  active: { label: "Active care", data: [11, 11, 12, 12, 12, 13, 13, 13, 14, 15, 15, 16] },
  outside: { label: "Outside rhythm", data: [17, 16, 16, 15, 14, 13, 12, 11, 10, 9, 9, 8] },
};

const Movement = ({ icon: Icon, children, onOpen, testid }) => (
  <button onClick={onOpen} className="w-full flex items-start gap-3 py-3 text-left group" data-testid={testid}>
    <Icon size={18} className="text-calm mt-1 shrink-0" />
    <span className="flex-1 text-[16px] text-ink leading-snug group-hover:text-brand transition-colors">{children}</span>
    <ArrowRight size={16} className="text-ink-faint mt-1 shrink-0" />
  </button>
);

const Changing = ({ m }) => {
  const { openList } = useProto();
  const [series, setSeries] = useState("connected");
  const scale = series === "outside" ? m.outside / 8 : 1;
  const data = SERIES[series].data.map((v, i) => ({ v: Math.round(v * scale), label: format(subWeeks(new Date(), 11 - i), "MMM d") }));
  const out4 = Math.round(11 * (m.outside / 8));
  return (
    <section data-testid="overview-changing">
      <H>What is changing?</H>
      <Card className="p-6 mt-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(SERIES).map(([k, s]) => (
            <button key={k} onClick={() => setSeries(k)} className={cn("h-9 px-4 rounded-full text-sm font-medium", series === k ? "bg-ink text-paper" : "bg-sunk text-ink-soft")} data-testid={`trend-${k}`}>{s.label}</button>
          ))}
        </div>
        <p className="text-sm text-ink-faint mt-4">Last 12 weeks</p>
        <div className="h-36 mt-2 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ left: 8, right: 8, top: 6, bottom: 0 }}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} interval={3} padding={{ left: 20, right: 20 }} tick={{ fill: "var(--ink-faint)", fontSize: 12 }} />
              <Area type="monotone" dataKey="v" stroke="var(--brand)" strokeWidth={2.5} fill="var(--brand-soft)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 border-t border-line divide-y divide-line">
          <Movement icon={TrendingUp} testid="move-connected" onOpen={() => openList("week")}>People connected each week is <b>up 19%</b> from a month ago (37 → {m.week}).</Movement>
          <Movement icon={HeartPulse} testid="move-active" onOpen={() => openList("active")}>Active care grew from 13 to {m.active} this month — mostly recovery and grief.</Movement>
          <Movement icon={TrendingDown} testid="move-outside" onOpen={() => openList("outside")}>People outside your rhythm fell from {out4} to {m.outside}.</Movement>
        </div>
      </Card>
    </section>
  );
};

const needsSupport = (l, s) => !!l.quiet || s.people > COMFORTABLE_LOAD || s.pastDue >= 2;

const supportStory = (l, s, roomy) => {
  if (l.quiet) return { head: `${l.first} may need support.`, body: `Other team members have recently helped care for the people entrusted to ${l.first}. A friendly check-in could mean a lot.`, ask: `Check in with ${l.first}`, done: `We'll remind you to check in with ${l.first} this week` };
  if (s.people > COMFORTABLE_LOAD) return { head: `${l.first} is carrying more than usual.`, body: `${roomy ? `${roomy.first} has room if you'd like to share the load.` : "Sharing a few people could lighten the load."}`, ask: "Share the load", open: true };
  return { head: `${l.first} may need a hand.`, body: "A few follow-ups have slipped past their date.", ask: "Offer a hand", done: `We'll remind you to reach out to ${l.first}` };
};

const Facts = ({ s }) => (
  <p className="text-[15px] text-ink-soft mt-1">
    {[`${s.people} people entrusted`, s.needs && `${s.needs} ${s.needs === 1 ? "needs" : "need"} attention`, s.pastDue && `${s.pastDue} follow-up${s.pastDue > 1 ? "s" : ""} past due`].filter(Boolean).join(" · ")}
  </p>
);

const GroupHead = ({ dot, label, count, hint, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <span className={cn("h-2.5 w-2.5 rounded-full mt-2 shrink-0", dot)} />
    <div><p className="text-[17px] text-ink font-semibold">{label} <span className="text-ink-faint font-normal">· {count}</span></p><p className="text-sm text-ink-faint mt-0.5">{hint}</p></div>
  </div>
);

const SupportCard = ({ l, s, roomy }) => {
  const { openList } = useProto();
  const st = supportStory(l, s, roomy);
  const act = () => (st.open ? openList(`leader:${l.id}`) : toast.success(st.done));
  return (
    <div className="surface p-6" data-testid={`leader-flag-${l.id}`}>
      <div className="flex items-center gap-4">
        <Avatar person={l} size={56} />
        <div className="min-w-0"><p className="t-name-lg text-[24px]">{leaderName(l.id)}</p><Facts s={s} /></div>
      </div>
      <div className="mt-5 rounded-2xl bg-sun-soft px-5 py-4 flex gap-3" data-testid={`leader-support-${l.id}`}>
        <HandHeart size={20} className="text-sun shrink-0 mt-0.5" />
        <p className="text-[16px] text-ink leading-relaxed"><span className="font-semibold">{st.head}</span> {st.body}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <button className={actionBtn} onClick={() => openList(`leader:${l.id}`)} data-testid={`leader-open-${l.id}`}>Open {l.first}'s people</button>
        <button className={actionBtn} onClick={act} data-testid={`leader-act-${l.id}`}>{st.ask}</button>
      </div>
    </div>
  );
};

const AttentionRow = ({ l, s }) => {
  const { openList } = useProto();
  return (
    <button onClick={() => openList(`leader:${l.id}`)} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={`load-row-${l.id}`}>
      <Avatar person={l} size={46} />
      <span className="flex-1 min-w-0">
        <span className="block text-[17px] text-ink font-medium leading-tight group-hover:text-brand transition-colors">{leaderName(l.id)}</span>
        <span className="block text-[15px] text-ink-soft mt-0.5">{s.people} people entrusted · {leaderSummary(s).text}</span>
      </span>
      <ArrowRight size={18} className="text-ink-faint group-hover:text-brand shrink-0" />
    </button>
  );
};

const CaughtUp = ({ l, s }) => {
  const { openList } = useProto();
  return (
    <button onClick={() => openList(`leader:${l.id}`)} className="w-full flex items-center gap-3 rounded-2xl bg-surface px-3 py-2.5 text-left hover:shadow-soft transition-shadow" data-testid={`load-row-${l.id}`}>
      <span className="relative shrink-0">
        <Avatar person={l} size={40} />
        <span className="absolute -right-0.5 -bottom-0.5 h-4 w-4 rounded-full bg-calm text-white flex items-center justify-center ring-2 ring-surface"><Check size={10} strokeWidth={3} /></span>
      </span>
      <span className="min-w-0"><span className="block text-ink font-medium leading-tight">{leaderName(l.id)}</span><span className="block text-sm text-ink-faint">{s.people} people entrusted</span></span>
    </button>
  );
};

const CareTeam = ({ m }) => {
  const { openList, rhythm, assigned, isPhone } = useProto();
  const rows = Object.values(LEADERS).sort((a, b) => a.last.localeCompare(b.last)).map((l) => ({ l, s: leaderStats(l.id, rhythm, assigned) }));
  const support = rows.filter((r) => needsSupport(r.l, r.s));
  const attention = rows.filter((r) => !needsSupport(r.l, r.s) && r.s.needs);
  const caught = rows.filter((r) => !needsSupport(r.l, r.s) && !r.s.needs);
  const roomy = [...caught].sort((a, b) => a.s.people - b.s.people)[0]?.l;
  const link = "text-ink font-semibold underline decoration-line underline-offset-4 hover:text-brand";
  return (
    <section data-testid="overview-care-team">
      <H>Your care team</H>
      <p className="text-lg text-ink-soft mt-2 leading-relaxed max-w-3xl">
        {rows.length} leaders are caring for <button className={link} onClick={() => openList("covered")} data-testid="team-covered-link">{m.covered} people</button>.{" "}
        <button className={link} onClick={() => openList("coverage")} data-testid="team-uncovered-link">{m.noCaregiver} people</button> still need someone.
      </p>
      <p className="text-sm text-ink-faint mt-2">Grouped by how the people in their care are doing — never ranked.</p>

      {support.length > 0 && (
        <div className="mt-10" data-testid="team-group-support">
          <GroupHead dot="bg-sun" label="May need support" count={support.length} hint="A conversation or a little help could go a long way." />
          <div className={cn("grid gap-4 mt-5", !isPhone && "grid-cols-2 gap-6")}>
            {support.map((r) => <SupportCard key={r.l.id} l={r.l} s={r.s} roomy={roomy} />)}
          </div>
        </div>
      )}

      <div className={cn("mt-12 gap-8", isPhone ? "space-y-12" : "grid grid-cols-2 items-start")}>
        {attention.length > 0 && (
          <div data-testid="team-group-attention">
            <GroupHead dot="bg-ink-faint" label="A few people need attention" count={attention.length} hint="Caring well — a few people are ready for a check-in or follow-up." />
            <div className="mt-4 border-y border-line divide-y divide-line">
              {attention.map((r) => <AttentionRow key={r.l.id} l={r.l} s={r.s} />)}
            </div>
          </div>
        )}
        {caught.length > 0 && (
          <div data-testid="team-group-caught-up">
            <GroupHead dot="bg-calm" label="All caught up" count={caught.length} hint="Everyone in their care is within your rhythm and follow-ups are on track." />
            <div className="mt-4 tint bg-calm-soft p-2 grid gap-1.5">
              {caught.map((r) => <CaughtUp key={r.l.id} l={r.l} s={r.s} />)}
            </div>
          </div>
        )}
      </div>
      <p className="text-sm text-ink-faint mt-8">Most leaders care for about {COMFORTABLE_LOAD} people comfortably.</p>
    </section>
  );
};

export default function PastorOverview() {
  const { isPhone, rhythm, assigned, openList } = useProto();
  const m = metrics(rhythm, assigned);
  const waiting = OVERVIEW.unassigned.filter((u) => !assigned[u.id]).length;
  return (
    <div className={cn("pb-36", isPhone ? "px-4 pt-8" : "max-w-7xl mx-auto px-10 pt-14")} data-testid="overview-screen">
      <div className={isPhone ? "px-1" : ""}>
        <Eyebrow>Riverside Community Church · Overview</Eyebrow>
        <h1 className="t-greeting mt-3">Good {partOfDay()}, David.</h1>
        <p className="text-xl text-ink-soft mt-4 leading-relaxed max-w-2xl">
          Care across Riverside is steady and growing.{" "}
          <button onClick={() => openList("attention")} className="text-ink font-semibold underline decoration-line underline-offset-4 hover:text-brand" data-testid="overview-attention-btn">{m.attention} people need attention</button>{" "}
          and {waiting} care {waiting === 1 ? "need is" : "needs are"} waiting for someone.
        </p>
        <p className="text-[15px] text-ink-faint mt-3">Care rhythm: every {rhythm} days · <RhythmPicker /></p>
      </div>
      <div className="mt-8"><Summary m={m} waiting={waiting} /></div>
      <div className={cn("mt-12", isPhone ? "space-y-12" : "grid grid-cols-[1.3fr_1fr] gap-8 items-start")}>
        <Gaps m={m} waiting={waiting} />
        <Changing m={m} />
      </div>
      <div className="mt-14"><CareTeam m={m} /></div>
    </div>
  );
}
